import { useCallback, useEffect, useState } from 'react';

import Filter, { FilterType } from './models/Filter';
import { Property, PropertyType } from './models/Property';

function createFilterFromProperty(property: Property): Filter {
  switch (property.type) {
    case PropertyType.Date:
      return {
        type: FilterType.Date,
        property,
        hasError: false,
        open: true,
        from: '',
        to: '',
      };
    case PropertyType.Dropdown:
      return {
        type: FilterType.Dropdown,
        hasError: false,
        property,
        open: true,
        relation: { value: '', label: '' },
        value: { value: '', label: '' },
      };
    case PropertyType.Number:
      return {
        type: FilterType.Number,
        hasError: false,
        property,
        open: true,
        relation: { value: '', label: '' },
        value: '',
      };
    default:
      return {
        type: FilterType.Text,
        hasError: false,
        property,
        open: true,
        relation: { value: '', label: '' },
        value: '',
      };
  }
}

const saveFilter = (oldFilter: Filter, newFilter: Filter): Filter => {
  switch (newFilter.type) {
    case FilterType.Date:
      return {
        ...oldFilter,
        ...newFilter,
        open: false,
        hasError: !newFilter.from || !newFilter.to,
      };
    case FilterType.Dropdown: {
      return {
        ...oldFilter,
        ...newFilter,
        open: false,
        hasError: !newFilter.relation?.value || !newFilter.value?.value,
      };
    }
    case FilterType.Number:
    case FilterType.Text:
      return {
        ...oldFilter,
        ...newFilter,
        open: false,
        hasError: !newFilter.relation || !newFilter.value,
      };
    default:
      return {
        ...oldFilter,
        open: false,
      };
  }
};

type FilterState = {
  /**
   * All filters
   */
  filters: Array<Filter>;
  handleClickProperty: (property: Property) => void;
  handleClickFilter: (filter: Filter) => void;
  handleDeleteFilter: (filter: Filter) => void;
  handleCloseFilterPopover: (oldFilter: Filter, newFilter: Filter) => void;
  /**
   * Filters that are ready to be used (no open filters and no filters with errors).
   */
  readyFilters: Array<Filter>;
};

const useSearchFilter = (initialFilters: Array<Filter> = []): FilterState => {
  const [filters, setFilters] = useState<Array<Filter>>(initialFilters);
  const [readyFilters, setReadyFilters] = useState<Array<Filter>>([]);
  useEffect(() => {
    if (!filters.find((filter) => filter.open || filter.hasError)) {
      setReadyFilters(filters);
    }
  }, [filters]);
  const handleClickProperty = useCallback(
    (property: Property) => {
      const existingFilter = filters.find((t) => t.property.name === property.name);
      if (existingFilter) {
        setFilters(filters.map((t) => ({ ...t, open: t.property.name === property.name })));
      } else {
        setFilters(filters.concat(createFilterFromProperty(property)));
      }
    },
    [filters, setFilters]
  );
  const handleClickFilter = useCallback(
    (filter: Filter) => {
      setFilters(
        filters.map((t) => (t.property.name !== filter.property.name ? t : { ...t, open: true }))
      );
    },
    [filters]
  );
  const handleDeleteFilter = useCallback(
    (filter: Filter) => {
      setFilters(filters.filter((t) => t.property.name !== filter.property.name));
    },
    [filters]
  );
  const handleCloseFilterPopover = useCallback(
    (_: Filter, newFilter: Filter) => {
      setFilters(
        filters.map((filter) => {
          if (newFilter?.property) {
            return filter.property.name !== newFilter.property.name
              ? filter
              : saveFilter(filter, newFilter);
          }

          return { ...filter, open: false };
        })
      );
    },
    [filters]
  );
  return {
    filters,
    handleClickProperty,
    handleClickFilter,
    handleDeleteFilter,
    handleCloseFilterPopover,
    readyFilters,
  };
};

export default useSearchFilter;
